import { ReactNode, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getBillingStatus, startTrial } from "@/api/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Gift } from "lucide-react";
import { ReferralModal } from "@/components/ReferralModal";
import HelpTooltip from "@/components/shared/HelpTooltip";
import { T, useT } from "@/lib/translation";
import { formatFixed, getIntlLocale } from "@/lib/locale";
import {
  carriedTrialEnd,
  formatSaleEndDate,
  getPlanPrices,
  getReferralCredit,
} from "@/lib/pricing";
import { PlanOption } from "@/components/billing/PlanOption";

type Plan = "monthly" | "yearly";

/**
 * The monthly/yearly plan picker that starts a Stripe checkout via `startTrial`.
 *
 * This is the one place a signed-in user actually subscribes. It is reused as a modal from
 * everywhere that offers "Subscribe": the settings subscription card and the free-tier
 * upgrade prompt on gated paid features. Neither of those should link out to the public
 * `/pricing` marketing page, which is written for signed-out visitors and only offers signup.
 */
export function SubscribeDialog({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const t = useT();

  const { data: status } = useQuery({
    queryKey: ["billing-info-status"],
    queryFn: getBillingStatus,
    enabled: open,
  });

  const isFounding = status?.is_founding_member ?? false;
  const prices = getPlanPrices(isFounding);
  const {
    monthly: monthlyPrice,
    yearly: yearlyPrice,
    yearlyList,
    onSale,
    yearlySavingsPercent,
    salePercentOff,
  } = prices;
  // A yearly sale checkout keeps the days left of an app trial, so the card is charged at its end.
  const trialEnd = carriedTrialEnd(
    onSale,
    status?.tier ?? "paid",
    status?.trial_ends_at ?? null,
  );

  const referralCopy =
    selectedPlan === "yearly"
      ? t("billing:referral.copyYearly")
      : selectedPlan === "monthly"
        ? t("billing:referral.copyMonthly")
        : t("billing:referral.copyNone");

  const pendingFreeMonths = status?.pending_free_months ?? 0;
  const { yearlyCredit, yearlyAfterCredit } = getReferralCredit(
    prices,
    pendingFreeMonths,
  );
  const pendingCreditCopy =
    pendingFreeMonths > 0
      ? selectedPlan === "yearly"
        ? t("billing:referral.bankedYearly", {
            count: pendingFreeMonths,
            credit: formatFixed(yearlyCredit, 2),
            price: formatFixed(yearlyAfterCredit, 2),
          })
        : selectedPlan === "monthly"
          ? t("billing:referral.bankedMonthly", { count: pendingFreeMonths })
          : t("billing:referral.bankedNone", { count: pendingFreeMonths })
      : null;

  const handleSubscribe = async () => {
    if (redirecting || !selectedPlan) return;
    setRedirecting(true);
    try {
      const url = await startTrial(selectedPlan);
      window.location.href = url;
    } catch {
      toast.error(t("billing:toasts.checkoutFailed"), {
        description: t("billing:toasts.tryAgainShortly"),
      });
      setRedirecting(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSelectedPlan(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("billing:subscribeDialog.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 text-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <PlanOption
              label={t("billing:plan.monthly")}
              price={t("billing:plan.perMonth", { price: monthlyPrice })}
              subLabel={t("billing:plan.billedMonthly")}
              vatNote={t("billing:plan.vat")}
              selected={selectedPlan === "monthly"}
              onSelect={() => setSelectedPlan("monthly")}
            />
            <PlanOption
              label={t("billing:plan.yearly")}
              price={t("billing:plan.perYear", { price: yearlyPrice })}
              originalPrice={
                onSale
                  ? t("billing:plan.perYear", { price: yearlyList })
                  : undefined
              }
              subLabel={t("billing:plan.worksOutTo", {
                price: formatFixed(yearlyPrice / 12, 2),
              })}
              vatNote={t("billing:plan.vat")}
              badge={t("billing:plan.saveBadge", {
                percent: yearlySavingsPercent,
              })}
              saleBadge={
                onSale
                  ? t("billing:plan.saleBadge", {
                      percent: salePercentOff,
                    })
                  : undefined
              }
              selected={selectedPlan === "yearly"}
              onSelect={() => setSelectedPlan("yearly")}
            />
          </div>
          {onSale && (
            <p className="-mt-3 text-xs text-muted-foreground">
              {t("billing:plan.saleNote", {
                date: formatSaleEndDate(),
                price: yearlyList,
              })}
            </p>
          )}

          {selectedPlan === "yearly" && trialEnd && (
            <p className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs text-foreground/80">
              {t("billing:plan.saleKeepsTrial", {
                date: trialEnd.toLocaleDateString(getIntlLocale(undefined), {
                  day: "numeric",
                  month: "long",
                }),
              })}
            </p>
          )}

          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
            <p className="flex items-center gap-1.5 text-xs font-bold tracking-[0.12em] uppercase text-primary mb-2">
              <Gift className="h-3.5 w-3.5" />
              {t("billing:referral.heading")}
              <HelpTooltip>
                <p>
                  <T
                    k="billing:referral.monthlyTip"
                    components={{ strong: <strong /> }}
                  />
                </p>
                <p>
                  <T
                    k="billing:referral.yearlyTip"
                    components={{ strong: <strong /> }}
                  />
                </p>
              </HelpTooltip>
            </p>
            <p className="text-sm text-foreground/80 leading-relaxed min-h-[4.875em]">
              {referralCopy}
            </p>
            <ReferralModal>
              <button className="mt-2 text-xs font-medium text-primary underline underline-offset-2 hover:no-underline">
                {t("billing:referral.getInviteLink")}
              </button>
            </ReferralModal>

            {pendingCreditCopy && (
              <p className="mt-3 pt-3 border-t border-primary/20 text-sm font-medium text-emerald-700">
                {pendingCreditCopy}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              onClick={handleSubscribe}
              disabled={redirecting || !selectedPlan}
            >
              {redirecting
                ? t("billing:actions.redirecting")
                : t("billing:actions.subscribe")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("billing:stripeNotice")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SubscribeDialog;
